import { reactive } from 'vue';
import { isEmpty, orderBy } from 'lodash-es';
import { useNotif } from '@orion.ui/orion';
import { TEMP_WORKER_DOCUMENT_TYPE } from '@/typings/enums/TEMP_WORKER_DOCUMENT_TYPE';
import { MOBILITY } from '@/typings/enums/MOBILITY';
import { enumKeys, formatPhone, toRequestType } from '@/utils/tools';
import { getReadableIdentityPieceType, identityPieceType, tempWorkerDocumentDescriptionsCore } from '@/utils/tempWorkerDocument';
import type { PictoType } from '@/setup/shared/SharedPictoCardSetupService';
import BaseEntity from '@/entity/BaseEntity';
import TaskEntity from '../TaskEntity';
import type OrderEntity from '../order/OrderEntity';
import useTempWorkerDocumentService from '@/services/temp-worker/TempWorkerDocumentService';
import useJustificatoryService from '@/services/JustificatoryService';
import useAvailabilityService from '@/services/AvailabilityService';
import useRequestedInformationService from '@/services/RequestedInformationService';
import { REQUESTED_INFORMATION_TYPE } from '@/typings/enums/REQUESTED_INFORMATION_TYPE';
import useBusService from '@/services/BusService';
import { USER_EVENT_TYPE } from '@/typings/enums/USER_EVENT_TYPE';

type TempWorkerDocumentForFront = ITempWorkerDocumentForCandidateDetailsModel & {
  callback?(): void
  tooltip?: string
  label?: string
  color?: Orion.Color
  data?: Record<string, any>
  requested?: boolean
}

type JustificatoryForFront = IJustificatoryModel & TempWorkerDocumentForFront & {
  type?: PictoType
  requested?: boolean
  marker?: boolean
}
export default class TempWorkerEntity extends BaseEntity<Armado.TempWorkerEntityType> {

	private readonly privateState = reactive({
		availabilities: undefined as Undef<IAvailabilityModel>,
		unavailabilities: [] as IUnavailabilityModel[],
		allJustificatories: [] as JustificatoryForFront[],
		alreadyRequestedDocuments: [] as IRequestedInformationForAgencyModel[],
	});

	get address () { return [this.state.personalInformationCity, this.state.personalInformationPostCode].filter(x => !!x?.length).join(', '); }
	get authorUserRating () { return this.state.authorUserRating; }
	get availabilities () { return this.privateState.availabilities; }
	get avatarId () { return this.state.avatarId; }
	get birthDay () { return this.state.personalInformationBirthDay ? new Date(this.state.personalInformationBirthDay) : undefined; }
	get city () { return this.state.personalInformationCity; }
	get email () { return this.state.email; }
	get firstName () { return this.state.firstName; }
	get hasPersonalVehicle () { return this.state.tempWorkerInformationHasPersonalVehicle; }
	get introduction () { return this.state.tempWorkerInformationIntroduction; }
	get lastName () { return this.state.lastName; }
	get mobilePhone () { return this.state.personalInformationMobilePhone; }
	get mobilePhoneFormatted () { return formatPhone(this.state.personalInformationMobilePhone); }
	get numberOfRating () { return this.state.numberOfRating; }
	get professionalExperiences () { return this.state.professionalExperiences; }
	get professionalSkills () { return this.state.professionalSkills; }
	get rating () { return this.state.rating; }
	get talentPoolIds () { return this.state.talents.mapKey('id'); }
	get tempWorkerDocuments () { return this.state.tempWorkerDocuments as TempWorkerDocumentForFront[]; }
	get userLanguages () { return this.state.userLanguages; }
	get userProfileComments () { return this.state.userProfileComments; }
	get userTasks () { return TaskEntity.map(this.state.userTasks).map(x => x.associateTempWorker(this)); }
	get profileIsPublic () { return this.state.profileIsPublic; }
	get userAgencyQualifications () { return this.state.userAgencyQualifications; }

	get createdDate () {
		const event = this.state.userEvents.find(x => x.type === USER_EVENT_TYPE.Created);
		if (event) return new Date(event.date);
	}

	get activationDate () {
		const event = this.state.userEvents.find(x => x.type === USER_EVENT_TYPE.Activated);
		if (event) return new Date(event.date);
	}

	get name () {
		return [
			this.state.firstName,
			this.state.lastName,
		].filter(x => !!x?.length).join(' ');
	}

	get namePartial () {
		return [
			this.state.firstName,
			this.state.lastName ? `${this.state.lastName[0]}.` : undefined,
		].filter(x => !!x?.length).join(' ');
	}

	get unavailabilities () {
		return this.privateState.unavailabilities.map(x => ({
			...x,
			endDate: new Date(x.endDate),
			startDate: new Date(x.startDate),
		}));
	}

	get orderedProfessionalExperiences () {
		return this.professionalExperiences.sort((a, b) => new Date(b.startDate).valueOf() - new Date(a.startDate).valueOf());
	}

	get documentsSanitized () {
		const readableDocumentTypes = enumKeys(TEMP_WORKER_DOCUMENT_TYPE).map((key) => {
			const icon = tempWorkerDocumentDescriptionsCore[TEMP_WORKER_DOCUMENT_TYPE[key]].icon;
			let label = tempWorkerDocumentDescriptionsCore[TEMP_WORKER_DOCUMENT_TYPE[key]].title;

			if (icon === 'IdentityPiece') {
				const identityPiece = this.tempWorkerDocuments.find(x => x.tempWorkerDocumentType === TEMP_WORKER_DOCUMENT_TYPE.IdentityPiece);
				if (identityPiece?.metaData) {
					const identityPieceMetaData = JSON.parse(identityPiece.metaData);
					Object.keys(identityPieceType).forEach((idType) => {
						const type = idType as keyof typeof identityPieceType;
						if (identityPieceType[type] === identityPieceMetaData.identityPieceType) {
							label = getReadableIdentityPieceType(identityPieceType[type]);
						}
					});
				}
			}

			return {
				tempWorkerDocumentType: TEMP_WORKER_DOCUMENT_TYPE[key],
				icon,
				label,
			};
		});

		const result = this.tempWorkerDocuments.map((x) => {
			const data: TempWorkerDocumentForFront = {
				...x,
				...(readableDocumentTypes.find(doc => doc.tempWorkerDocumentType === x.tempWorkerDocumentType) ?? {}),
				data: JSON.parse(x.metaData ?? '{}'),
			};

			if (this.self.isAgency()) {
				data.callback = () => useTempWorkerDocumentService().view(x.id);
				data.tooltip = `Visualiser`;
				data.color = 'info';
			}
			return data;
		});

		result.unshift(...this.missingJustificatories);

		return result;
	}

	get justificatories () {
		return this.documentsSanitized.filter((x) => {
			return x.tempWorkerDocumentType !== TEMP_WORKER_DOCUMENT_TYPE.Diploma && x.tempWorkerDocumentType !== TEMP_WORKER_DOCUMENT_TYPE.Custom;
		}) as JustificatoryForFront[];
	}

	get missingJustificatories () {
		const missingJustificatories: JustificatoryForFront[] = [];

		this.privateState.allJustificatories
			.filter((x) => {
				return x.tempWorkerDocumentType !== TEMP_WORKER_DOCUMENT_TYPE.Diploma && x.tempWorkerDocumentType !== TEMP_WORKER_DOCUMENT_TYPE.Custom;
			})
			.forEach((justif) => {
				if (!this.tempWorkerDocuments.find(x => x.tempWorkerDocumentType === justif.tempWorkerDocumentType)) {
					const requested = this.alreadyRequested(toRequestType(justif.tempWorkerDocumentType));
					const isMissingForOrder = this.privateState.alreadyRequestedDocuments
						.find(w => w.requestedInformationType === toRequestType(justif.tempWorkerDocumentType));

					justif.marker = !!isMissingForOrder;
					justif.requested = requested;
					justif.tooltip = requested
						? `Vous avez demandé le partage du document le ${
							this.requestedInformationDate(toRequestType(justif.tempWorkerDocumentType))
						}`
						: 'Demander le partage du document';
					// justif.color = 'warning';

					if (!requested) {
						justif.callback = async () => {
							await useRequestedInformationService().addAsync({
								userId: this.id,
								requestedInformationType: toRequestType(justif.tempWorkerDocumentType),
							});
							useNotif.success('Demande de partage', `La demande de partage du document ${justif.label} a bien été envoyée`);
							this.loadAlreadyRequestedDocumentsAsync();
						};
					}
					if (requested) {
						justif.callback = async () => {
							await useRequestedInformationService().updateAsync({
								userId: this.id,
								requestedInformationType: toRequestType(justif.tempWorkerDocumentType),
							});
							useNotif.success('Demande de partage', `La demande de partage du document ${justif.label} a bien été envoyée`);
							this.loadAlreadyRequestedDocumentsAsync();
						};
					}
					missingJustificatories.push(justif);
				}
			});

		return missingJustificatories;
	}

	get diplomas () {
		if (!this.documentsSanitized.filter(x => x.tempWorkerDocumentType === TEMP_WORKER_DOCUMENT_TYPE.Diploma).length) {
			const requested = this.alreadyRequested(toRequestType(TEMP_WORKER_DOCUMENT_TYPE.Diploma));
			return [
				{
					id: 0,
					color: requested ? 'warning' : 'default',
					label: 'Diplômes',
					tooltip: requested
						? `Vous avez demandé le partage de diplômes le ${
							this.requestedInformationDate(toRequestType(TEMP_WORKER_DOCUMENT_TYPE.Diploma))
						}`
						: 'Demander le partage de diplômes',
					requested: requested,
					tempWorkerDocumentType: 5,
					icon: 'certified-diploma',
					callback: this.requestForDiplomaAsync.bind(this),
				} as TempWorkerDocumentForFront,
			];
		} else {
			return this.documentsSanitized
				.filter(x => x.tempWorkerDocumentType === TEMP_WORKER_DOCUMENT_TYPE.Diploma)
				.map(x => ({
					...x,
					label: x.data?.title ?? x.label,
				}));
		}
	}

	get customDocuments () {
		return this.documentsSanitized
			.filter(x => x.tempWorkerDocumentType === TEMP_WORKER_DOCUMENT_TYPE.Custom)
			.map(x => ({
				...x,
				label: x.data?.title ?? x.label,
			})); ;
	}

	get mobility () {
		switch (this.state.tempWorkerInformationMobility) {
		case (MOBILITY.Lte10Km):
			return 'moins de 10km';
		case (MOBILITY.Lte30Km):
			return 'moins de 30km';
		case (MOBILITY.Lte50Km):
			return 'moins de 50km';
		case (MOBILITY.Gte50Km):
			return 'plus de 50km';
		default: return null;
		}
	}

	get isAvailableNow () {
		// TODO: vérifier le lien entre dispo et availabilityDate
		if (!this.privateState.availabilities?.availabilityDate) return false;

		const date = new Date(this.privateState.availabilities.availabilityDate);
		date.setHours(0, 0, 0, 0);

		const now = new Date();
		now.setHours(0, 0, 0, 0);

		return date.valueOf() <= now.valueOf();
	}

	get availabilityLabel () {
		if (!this.privateState.availabilities || !this.privateState.availabilities?.availabilityDate) return '';
		if (this.isAvailableNow) return 'dès maintenant';

		const date = new Date(this.privateState.availabilities.availabilityDate);
		return `à partir du ${date.toLocaleDateString()}`;
	}

	get orderedUnavailabilities () {
		return orderBy(this.unavailabilities, x => new Date(x.startDate));
	}

	get requestedAvailabilityDate () {
		const request = this.privateState.alreadyRequestedDocuments.find(x => x.requestedInformationType === REQUESTED_INFORMATION_TYPE.Availability);
		if (request) return new Date(request.requestedDate);
	}

	get requestPublicProfileDate () {
		const request = this.privateState.alreadyRequestedDocuments.find(x => x.requestedInformationType === REQUESTED_INFORMATION_TYPE.PublicProfile);
		if (request) return new Date(request.requestedDate);
	}

	/* constructor (payload?: Armado.TempWorkerEntityType) {
		super(payload);
	} */

	protected onDataAssigned (): void {
		useBusService().on(`${this.id}-details:ranking-update`, (rating: number) => {
			this.state.rating = rating;
		});
	}


	async loadAllJustificatoriesAsync () {
		this.privateState.allJustificatories = await useJustificatoryService().listAsync();
	}

	async loadAlreadyRequestedDocumentsAsync () {
		const data = await useRequestedInformationService().listAsync(this.id);
		this.privateState.alreadyRequestedDocuments = data;
	}

	async loadAvailabilityAsync () {
		const data = await useAvailabilityService().getAsync(this.id);
		this.privateState.availabilities = isEmpty(data) ? undefined : data;
	}

	async loadUnavailabilitiesAsync () {
		this.privateState.unavailabilities = await useAvailabilityService().listUnavailabilitiesByUserIdAsync(this.id);
	}

	hasRequiredOrderProfessionalSkill (order: OrderEntity) {
		return !!this.professionalSkills.find(x => x.id === order.professionalSkill?.id);
	}

	alreadyRequested (requestType: REQUESTED_INFORMATION_TYPE) {
		return !!this.privateState.alreadyRequestedDocuments.findByKey(requestType, 'requestedInformationType');
	}

	requestedInformationDate (requestType: REQUESTED_INFORMATION_TYPE) {
		const targetDocument = this.privateState.alreadyRequestedDocuments.find(x => x.requestedInformationType === requestType);
		if (targetDocument) return new Date(targetDocument.requestedDate).toReadable();
	}

	async requestForDiplomaAsync () {
		const alreadyRequested = !!this.privateState.alreadyRequestedDocuments
			.find(x => x.requestedInformationType === toRequestType(TEMP_WORKER_DOCUMENT_TYPE.Diploma));

		alreadyRequested
			? await useRequestedInformationService().updateAsync({
				userId: this.id,
				requestedInformationType: toRequestType(TEMP_WORKER_DOCUMENT_TYPE.Diploma),
			})
			: await useRequestedInformationService().addAsync({
				userId: this.id,
				requestedInformationType: toRequestType(TEMP_WORKER_DOCUMENT_TYPE.Diploma),
			});

		await this.loadAlreadyRequestedDocumentsAsync();
		useNotif.success('Demande envoyée', `La demande de partage de Diplôme a bien été envoyée`);
	}

	async requestForPublicProfileAsync () {
		const alreadyRequested = !!this.privateState.alreadyRequestedDocuments
			.find(x => x.requestedInformationType === toRequestType(REQUESTED_INFORMATION_TYPE.PublicProfile));

		alreadyRequested
			? await useRequestedInformationService().updateAsync({
				userId: this.id,
				requestedInformationType: toRequestType(REQUESTED_INFORMATION_TYPE.PublicProfile),
			})
			: await useRequestedInformationService().addAsync({
				userId: this.id,
				requestedInformationType: toRequestType(REQUESTED_INFORMATION_TYPE.PublicProfile),
			});
		await this.loadAlreadyRequestedDocumentsAsync();

		useNotif.success(`La demande de partage du profil a bien été envoyée`);
	}

	addTask (task: IUserTaskModel) {
		this.state.userTasks.push(task);
	}

	deleteTask (id: number) {
		this.state.userTasks.deleteWhere('id', id);
	}
}
